import { BUCKETS, PAIRS, bucketOf, pairKey, type Axis, type Band, type PartProfile }
  from './partProfile';

// TAKING A PART'S PROFILE OFF ITS MESH — the half of partProfile that needs the geometry.
// Kept apart from the record it produces because everything that READS a profile (holePlace,
// and Phase 4's table through it) is code with no 3D in it, and one import would have put a
// renderer in front of a table of millimetres.
//
// AND `three` IS A TYPE HERE AND NOT A VALUE, which is the same trick assemblyClash uses and
// for a sharper reason than tidiness. Written with `import * as THREE`, this file made three
// reachable from the eager graph and rollup answered by moving 385 kB of it out of the lazy
// chunk and into the entry — measured, not guessed. Everything below is a dot product and a
// min/max, so the library was buying two constructors.
type Object3D = import('three').Object3D;
type Mesh = import('three').Mesh;
type BufferAttribute = import('three').BufferAttribute;

const AXIS_AT: Record<Axis, number> = { x: 0, y: 1, z: 2 };

// EVERY TRIANGLE, NOT EVERY VERTEX. A reconstruction has enough vertices that either would
// do; a CAD conversion of a rail has eight, so bucketing vertices leaves thirty of the
// thirty-two buckets empty and the part reads as two specks of wood at its ends.
//
// A triangle contributes its whole span along the measured axis to every bucket it touches,
// which over-reports slightly on a long diagonal face. It over-reports OUTWARDS — the
// envelope only ever grows — so the error is on the side of saying there is wood where the
// surface has already sloped away, never of putting a bore outside the part.
//
// THE BOX IS THE CALLER'S, because the caller has just measured it: this is called beside the
// measurement that feeds the part's size record, off the same object in the same frame, and
// measuring it twice would be two boxes to keep in step for no gain.
export function sampleProfile(
  root: Object3D,
  box: { min: [number, number, number]; size: [number, number, number] },
): PartProfile | null {
  if (!box.size.every((s) => s > 0)) return null;
  const bands: Record<string, Band[]> = {};
  const pairs = PAIRS;
  for (const [a, b] of pairs) bands[pairKey(a, b)] = new Array<Band>(BUCKETS).fill(null);

  // The triangle's three corners in the root's own frame, reused rather than allocated: a
  // tessellated part is a hundred thousand of these.
  const p = [0, 0, 0, 0, 0, 0, 0, 0, 0];
  root.traverse((node) => {
    const mesh = node as Mesh;
    if (!mesh.isMesh || !mesh.geometry) return;
    const pos = mesh.geometry.attributes?.position as BufferAttribute | undefined;
    if (!pos) return;
    const index = mesh.geometry.index;
    const count = index ? index.count : pos.count;
    // Column-major, and applied by hand — an affine transform is nine multiplies and three
    // adds, and the alternative is importing a matrix class to do the same nine.
    const m = mesh.matrixWorld.elements;
    for (let i = 0; i + 2 < count; i += 3) {
      for (let k = 0; k < 3; k += 1) {
        const j = index ? index.getX(i + k) : i + k;
        const x = pos.getX(j);
        const y = pos.getY(j);
        const z = pos.getZ(j);
        p[k * 3] = m[0] * x + m[4] * y + m[8] * z + m[12];
        p[k * 3 + 1] = m[1] * x + m[5] * y + m[9] * z + m[13];
        p[k * 3 + 2] = m[2] * x + m[6] * y + m[10] * z + m[14];
      }
      for (const [a, b] of pairs) {
        const ai = AXIS_AT[a];
        const bi = AXIS_AT[b];
        const aLo = Math.min(p[ai], p[3 + ai], p[6 + ai]);
        const aHi = Math.max(p[ai], p[3 + ai], p[6 + ai]);
        const bLo = (Math.min(p[bi], p[3 + bi], p[6 + bi]) - box.min[bi]) / box.size[bi];
        const bHi = (Math.max(p[bi], p[3 + bi], p[6 + bi]) - box.min[bi]) / box.size[bi];
        const from = bucketOf((aLo - box.min[ai]) / box.size[ai]);
        const to = bucketOf((aHi - box.min[ai]) / box.size[ai]);
        const row = bands[pairKey(a, b)];
        for (let t = from; t <= to; t += 1) {
          const had = row[t];
          row[t] = had ? [Math.min(had[0], bLo), Math.max(had[1], bHi)] : [bLo, bHi];
        }
      }
    }
  });

  // Rounded on the way out, because this is written into a document several people share and
  // four decimals of a fraction is a tenth of a millimetre on a metre-long part.
  for (const key of Object.keys(bands)) {
    bands[key] = bands[key].map((band) => (band
      ? [Math.round(band[0] * 1e4) / 1e4, Math.round(band[1] * 1e4) / 1e4] as Band
      : null));
  }
  return { buckets: BUCKETS, bands };
}
